import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo } from "react";
import type {
  CustomFieldDataType,
  CustomFieldDefinition,
} from "@/types/custom-fields";
import type { EntityType } from "@/types/entity";
import type { UnifiedTableColumn } from "@/types/table-column";
import { useFieldDefinitions } from "./api/use-field-definitions";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

export interface SelectableColumn {
  id: string;
  title: string;
  columnType: "core" | "custom" | "metric" | "ai";
  required?: boolean;
  customField?: CustomFieldDefinition;
  dataType?: CustomFieldDataType;
  defaultHidden?: boolean;
}

/**
 * Hook for managing selection of both core and custom columns for a specific entity type.
 * Persists selections to sessionStorage for tab-specific behavior.
 * Designed to work with TanStack React Table column visibility API.
 *
 * @param entity - The type of record to get field definitions for (e.g., "accounts")
 * @param coreColumns - The list of core columns for this entity
 * @param storageKeyPrefix - Prefix to use for sessionStorage persistence
 * @param selectedMetrics - Optional array of selected metric keys (for entities that support metrics)
 * @returns Methods and state for managing selected columns
 */
export function useColumnSelection<T = Record<string, unknown>>(
  entity: EntityType,
  coreColumns: UnifiedTableColumn<T>[],
  storageKeyPrefix: string,
  selectedMetrics?: string[],
) {
  // Keys for sessionStorage
  const columnsVisibilityKey = `${storageKeyPrefix}::list::column-visibility`;
  const customFieldsStorageKey = `${storageKeyPrefix}::list::custom-fields`;

  const {
    data: fdData = [],
    isLoading: isLoadingCustomFields,
    error: customFieldsError,
  } = useFieldDefinitions(entity);

  const customFieldDefs: CustomFieldDefinition[] = useMemo(
    () =>
      fdData
        .filter((fd) => fd.is_custom) // Keep only custom fields for this list
        .map((fd) => ({
          id: fd.id,
          display_name: fd.display_name,
          data_type: fd.data_type as CustomFieldDataType,
          record_type: fd.record_type || "",
          value_enum: fd.enum_values || undefined,
        })),
    [fdData],
  );

  // Store column visibility state in sessionStorage (matches TanStack's VisibilityState)
  const [columnVisibility, setColumnVisibility] = useTrackedSessionStorage<
    Record<string, boolean>
  >(
    columnsVisibilityKey,
    // By default, show core columns but hide those marked as defaultHidden or formula columns
    coreColumns
      .filter((col) => !col.exportOnly)
      .reduce(
        (acc, col) => {
          const colId = col.key.toString();
          // Check if column should be hidden by default
          if (col.defaultHidden === true) {
            acc[colId] = false;
          }
          // Formula columns should be hidden by default
          else if (colId.startsWith("formula_")) {
            acc[colId] = false;
          } else {
            // Always set required columns to true, otherwise use default (true) for core columns
            acc[colId] = true;
          }
          return acc;
        },
        {} as Record<string, boolean>,
      ),
  );

  // Ensure required columns are always visible, even if localStorage had them as hidden
  useEffect(() => {
    setColumnVisibility((prev) => {
      let changed = false;
      const updated = { ...prev };

      // Check each required column and make sure it's visible
      coreColumns.forEach((col) => {
        if (col.required && !updated[col.key.toString()]) {
          updated[col.key.toString()] = true;
          changed = true;
        }
      });

      // Only update if something changed
      return changed ? updated : prev;
    });
  }, [coreColumns, setColumnVisibility]);

  // Store selected custom field IDs separately in sessionStorage
  const [selectedCustomFieldIds, setSelectedCustomFieldIds] =
    useTrackedSessionStorage<string[]>(customFieldsStorageKey, []);

  // Convert core columns to a standard format matching the selection interface
  const availableCoreColumns = useMemo<SelectableColumn[]>(() => {
    // Filter fdData to get only non-custom fields for core columns
    const serverCoreFields = fdData.filter((fd) => !fd.is_custom);

    return coreColumns
      .filter((col) => !col.exportOnly) // Don't include export-only columns
      .map((col) => {
        const serverField = serverCoreFields.find(
          (sf) => sf.id === col.key.toString(),
        );
        return {
          id: col.key.toString(),
          title: col.title,
          columnType: "core" as const,
          required: col.required === true, // Preserve the required property from the source column
          // Include dataType from serverField if available, otherwise undefined
          dataType: serverField?.data_type as CustomFieldDataType | undefined,
          defaultHidden: col.defaultHidden,
        };
      });
  }, [coreColumns, fdData]);

  // Convert custom fields to the same format
  const availableCustomColumns = useMemo<SelectableColumn[]>(() => {
    return customFieldDefs.map((field) => ({
      id: field.id,
      title: field.display_name,
      columnType: "custom" as const,
      customField: field,
    }));
  }, [customFieldDefs]);

  // Create metric columns as available columns (if metrics are provided)
  const availableMetricColumns = useMemo<SelectableColumn[]>(() => {
    if (!selectedMetrics || selectedMetrics.length === 0) return [];

    // Metrics are already selected, so we just need to track them
    // The actual column definitions will be created elsewhere
    return selectedMetrics.map((metricKey) => ({
      id: `metric_${metricKey}`,
      title: metricKey, // Title doesn't matter here as it's handled elsewhere
      columnType: "metric" as const,
    }));
  }, [selectedMetrics]);

  // Combine all types of columns
  const allAvailableColumns = useMemo<SelectableColumn[]>(() => {
    return [
      ...availableCoreColumns,
      ...availableCustomColumns,
      ...availableMetricColumns,
    ];
  }, [availableCoreColumns, availableCustomColumns, availableMetricColumns]);

  // Create a list of selected core columns
  const selectedCoreColumns = useMemo(() => {
    return availableCoreColumns.filter(
      (col) => columnVisibility[col.id] === true,
    );
  }, [availableCoreColumns, columnVisibility]);

  // Create a list of selected custom columns
  const selectedCustomColumns = useMemo(() => {
    return availableCustomColumns.filter((col) =>
      selectedCustomFieldIds.includes(col.id),
    );
  }, [availableCustomColumns, selectedCustomFieldIds]);

  // All selected columns combined
  const allSelectedColumns = useMemo(() => {
    return [
      ...selectedCoreColumns,
      ...selectedCustomColumns,
      ...availableMetricColumns,
    ];
  }, [selectedCoreColumns, selectedCustomColumns, availableMetricColumns]);

  // Toggle a column's visibility (for core columns)
  const toggleCoreColumnVisibility = useCallback(
    (columnId: string) => {
      setColumnVisibility((prev) => {
        const visibility = (prev || {}) as Record<string, boolean>;

        // Find the column to check if it's required
        const column = availableCoreColumns.find((col) => col.id === columnId);

        // If it's required, don't allow hiding it
        if (column?.required && visibility[columnId] === true) {
          return visibility;
        }

        // Check if this would be hiding the last visible column
        const isHiding = visibility[columnId] === true;

        if (isHiding) {
          // Count currently visible columns (both core and custom)
          const visibleCoreColumnCount =
            Object.values(visibility).filter(Boolean).length;
          const visibleCustomColumnCount = selectedCustomFieldIds.length;
          const visibleMetricColumnCount = selectedMetrics?.length || 0;
          const totalVisibleColumns =
            visibleCoreColumnCount +
            visibleCustomColumnCount +
            visibleMetricColumnCount;

          // Don't allow hiding the last column
          if (totalVisibleColumns <= 1) {
            return visibility;
          }
        }

        // Create a new object to ensure state updates properly
        return {
          ...visibility,
          [columnId]: visibility[columnId] !== true,
        };
      });
    },
    [
      availableCoreColumns,
      setColumnVisibility,
      selectedCustomFieldIds,
      selectedMetrics,
    ],
  );

  // Toggle a custom field's selection
  const toggleCustomColumnSelection = useCallback(
    (fieldId: string) => {
      setSelectedCustomFieldIds((prev) => {
        const current = (prev || []) as string[];

        // Check if we're removing this field
        const isRemoving = current.includes(fieldId);

        if (isRemoving) {
          // Count visible core columns
          const visibleCoreColumnCount =
            Object.values(columnVisibility).filter(Boolean).length;
          const visibleMetricColumnCount = selectedMetrics?.length || 0;

          // If this is the last visible column overall, don't remove it
          if (
            visibleCoreColumnCount === 0 &&
            current.length <= 1 &&
            visibleMetricColumnCount === 0
          ) {
            return current;
          }

          return current.filter((id) => id !== fieldId);
        } else {
          return [...current, fieldId];
        }
      });
    },
    [setSelectedCustomFieldIds, columnVisibility, selectedMetrics],
  );

  // Replace custom field selection with an explicit set of IDs (idempotent)
  const replaceCustomSelection = useCallback(
    (ids: string[]) => {
      const validIds = ids.filter((id) =>
        availableCustomColumns.some((col) => col.id === id),
      );
      setSelectedCustomFieldIds(validIds);
    },
    [availableCustomColumns, setSelectedCustomFieldIds],
  );

  // Toggle any column (whether core or custom)
  const toggleColumn = useCallback(
    (columnId: string) => {
      // Determine if this is a core column or custom field
      const isCoreColumn = availableCoreColumns.some(
        (col) => col.id === columnId,
      );

      if (isCoreColumn) {
        toggleCoreColumnVisibility(columnId);
      } else {
        toggleCustomColumnSelection(columnId);
      }
    },
    [
      availableCoreColumns,
      toggleCoreColumnVisibility,
      toggleCustomColumnSelection,
    ],
  );

  // Reset to default visibility (respecting defaultHidden and formula columns)
  const resetToDefault = useCallback(() => {
    // Set core columns to their default visibility state
    const defaultVisibility = availableCoreColumns.reduce(
      (acc, col) => {
        // Check if column should be hidden by default
        if (col.defaultHidden === true) {
          acc[col.id] = false;
        }
        // Formula columns should be hidden by default
        else if (col.id.startsWith("formula_")) {
          acc[col.id] = false;
        } else {
          // Always set required columns to true, otherwise default to visible
          acc[col.id] = true;
        }
        return acc;
      },
      {} as Record<string, boolean>,
    );

    setColumnVisibility(defaultVisibility);
    setSelectedCustomFieldIds([]);
  }, [availableCoreColumns, setColumnVisibility, setSelectedCustomFieldIds]);

  // Clear all selected columns and only show required ones or at least one column
  const clearSelection = useCallback(() => {
    // Get required columns
    const requiredColumns = availableCoreColumns.filter((col) => col.required);

    // Create initial state where only required columns are visible
    const minimalVisibility = availableCoreColumns.reduce(
      (acc, col) => {
        acc[col.id] = !!col.required;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    // If no required columns, make the first column visible to ensure at least one column is shown
    if (requiredColumns.length === 0 && availableCoreColumns.length > 0) {
      minimalVisibility[availableCoreColumns[0].id] = true;
    }

    setColumnVisibility(minimalVisibility);
    setSelectedCustomFieldIds([]);
  }, [availableCoreColumns, setColumnVisibility, setSelectedCustomFieldIds]);

  // Check if a column is selected/visible
  const isColumnSelected = useCallback(
    (columnId: string) => {
      // Check if it's a core column
      if (availableCoreColumns.some((col) => col.id === columnId)) {
        return columnVisibility[columnId] === true;
      }
      // Otherwise treat as a custom field
      return selectedCustomFieldIds.includes(columnId);
    },
    [availableCoreColumns, columnVisibility, selectedCustomFieldIds],
  );

  // Create a TanStack Table-compatible visibility state object
  const columnVisibilityState = useMemo(() => {
    // Start with core column visibility
    const visibilityState = { ...columnVisibility };

    // Add custom fields (selected = visible, not selected = hidden)
    for (const column of availableCustomColumns) {
      visibilityState[column.id] = selectedCustomFieldIds.includes(column.id);
    }

    // Add metric columns (always visible when in selectedMetrics)
    for (const column of availableMetricColumns) {
      visibilityState[column.id] = true;
    }

    return visibilityState;
  }, [
    columnVisibility,
    availableCustomColumns,
    selectedCustomFieldIds,
    availableMetricColumns,
  ]);

  // Extract custom field IDs for API requests
  const customFieldIdsParam = useMemo(() => {
    return selectedCustomFieldIds.join(",");
  }, [selectedCustomFieldIds]);

  // Function to get visible column definitions - memoised to avoid new identity each render
  const getVisibleColumnDefs = useCallback(
    <T>(columnDefs: ColumnDef<T>[]): ColumnDef<T>[] => {
      // First, extract all column IDs that should be visible according to our visibility state
      const visibleColumnIds = new Set([
        // Core columns from columnVisibility
        ...Object.entries(columnVisibility)
          .filter(([, isVisible]) => isVisible)
          .map(([id]) => id),
        // Custom fields from selectedCustomFieldIds
        ...selectedCustomFieldIds,
        // Metric columns from selectedMetrics
        ...(selectedMetrics?.map((m) => `metric_${m}`) || []),
      ]);

      // Filter to only include visible (or required) columns
      return columnDefs.filter((col) => {
        const colId = col.id || "";

        const meta = col.meta as { required?: boolean } | undefined;
        if (meta?.required) return true;

        return visibleColumnIds.has(colId);
      });
    },
    [columnVisibility, selectedCustomFieldIds, selectedMetrics],
  );

  return {
    // State
    availableColumns: allAvailableColumns,
    selectedColumns: allSelectedColumns,
    selectedCoreColumns,
    selectedCustomColumns,
    columnVisibility: columnVisibilityState, // TanStack compatible visibility state
    customFieldIdsParam,
    isLoading: isLoadingCustomFields,
    error: customFieldsError,

    // Actions
    toggleColumn,
    clearSelection,
    resetToDefault,
    isColumnSelected,
    replaceCustomSelection,

    // TanStack Table specific
    setColumnVisibility: (
      updater:
        | Record<string, boolean>
        | ((state: Record<string, boolean>) => Record<string, boolean>),
    ) => {
      if (typeof updater === "function") {
        setColumnVisibility((prev) => updater(prev || {}));
      } else {
        setColumnVisibility((prev: Record<string, boolean> | undefined) => ({
          ...(prev || {}),
          ...updater,
        }));
      }
    },

    // Function to get visible column definitions - memoised to avoid new identity each render
    getVisibleColumnDefs,
  };
}

/**
 * Hook specifically for managing account column selections.
 *
 * @param coreColumns - The core column definitions for accounts
 * @returns Methods and state for managing selected account columns
 */
export function useAccountColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
) {
  return useColumnSelection<T>("accounts", coreColumns, "accounts");
}

/**
 * Hook specifically for managing opportunity column selections.
 *
 * @param coreColumns - The core column definitions for opportunities
 * @returns Methods and state for managing selected opportunity columns
 */
export function useOpportunityColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
) {
  return useColumnSelection<T>("opportunities", coreColumns, "opportunities");
}

/**
 * Hook specifically for managing person column selections.
 *
 * @param coreColumns - The core column definitions for persons
 * @returns Methods and state for managing selected person columns
 */
export function usePersonColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
) {
  return useColumnSelection<T>("persons", coreColumns, "persons");
}

/**
 * Hook specifically for managing deep research column selections.
 *
 * @param entityType - The entity type for the deep research dataset
 * @param coreColumns - The core column definitions for the entity
 * @returns Methods and state for managing selected deep research columns
 */
export function useDeepResearchColumnSelection<T = Record<string, unknown>>(
  entityType: EntityType,
  coreColumns: UnifiedTableColumn<T>[],
) {
  return useColumnSelection<T>(
    entityType,
    coreColumns,
    `deep-research::${entityType}`,
  );
}

/**
 * Hook specifically for managing campaign column selections.
 *
 * @param coreColumns - The core column definitions for campaigns
 * @param selectedMetrics - Optional array of selected metric keys
 * @param formulaColumns - Optional array of formula columns to include in visibility management
 * @returns Methods and state for managing selected campaign columns
 */
export function useCampaignColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
  selectedMetrics?: string[],
  formulaColumns?: UnifiedTableColumn<T>[],
) {
  // Combine core columns with formula columns for unified visibility management
  const allCoreColumns = useMemo(() => {
    const combined = [...coreColumns];
    if (formulaColumns && formulaColumns.length > 0) {
      combined.push(...formulaColumns);
    }
    return combined;
  }, [coreColumns, formulaColumns]);

  return useColumnSelection<T>(
    "campaigns",
    allCoreColumns,
    "campaigns",
    selectedMetrics,
  );
}

/**
 * Hook specifically for managing webpage column selections.
 *
 * @param coreColumns - The core column definitions for webpages
 * @param selectedMetrics - Optional array of selected metric keys
 * @returns Methods and state for managing selected webpage columns
 */
export function useWebpageColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
  selectedMetrics?: string[],
) {
  return useColumnSelection<T>(
    "webpages",
    coreColumns,
    "webpages",
    selectedMetrics,
  );
}

/**
 * Hook specifically for managing salesperson column selections.
 * Note: Salespeople don't have custom fields, so we provide a simplified version.
 *
 * @param coreColumns - The core column definitions for salespeople
 * @returns Methods and state for managing selected salesperson columns
 */
export function useSalespersonColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
) {
  // Keys for sessionStorage
  const columnsVisibilityKey = `salespeople::list::column-visibility`;

  const [columnVisibility, setColumnVisibility] = useTrackedSessionStorage<
    Record<string, boolean>
  >(
    columnsVisibilityKey,
    // By default, show all core columns
    coreColumns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  // Convert core columns to SelectableColumn format
  const availableCoreColumns = useMemo<SelectableColumn[]>(
    () =>
      coreColumns
        .filter((col) => !col.exportOnly)
        .map((col) => ({
          id: col.key.toString(),
          title: col.title,
          columnType: "core" as const,
          required: col.required || false,
          defaultHidden: col.defaultHidden,
        })),
    [coreColumns],
  );

  // All available columns (core only, no custom fields for salespeople)
  const availableColumns = useMemo<SelectableColumn[]>(
    () => [...availableCoreColumns],
    [availableCoreColumns],
  );

  // Selected columns
  const selectedColumns = useMemo<SelectableColumn[]>(
    () =>
      availableCoreColumns.filter((col) => columnVisibility[col.id] === true),
    [availableCoreColumns, columnVisibility],
  );

  // Toggle core column visibility
  const toggleColumn = useCallback(
    (columnId: string) => {
      // Check if it's a required column
      const column = availableCoreColumns.find((col) => col.id === columnId);
      if (column?.required) return; // Can't toggle required columns

      // Check if this would be hiding the last visible column
      const isHiding = columnVisibility[columnId] === true;
      if (isHiding) {
        const visibleColumnCount =
          Object.values(columnVisibility).filter(Boolean).length;
        if (visibleColumnCount <= 1) {
          return; // Don't hide the last column
        }
      }

      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !(prev?.[columnId] ?? false),
      }));
    },
    [availableCoreColumns, setColumnVisibility, columnVisibility],
  );

  // Check if a column is selected/visible
  const isColumnSelected = useCallback(
    (columnId: string) => {
      return columnVisibility[columnId] === true;
    },
    [columnVisibility],
  );

  // Reset to default (respecting defaultHidden property)
  const resetToDefault = useCallback(() => {
    const defaultVisibility = availableCoreColumns.reduce(
      (acc, col) => {
        // Check if column should be hidden by default
        if (col.defaultHidden === true) {
          acc[col.id] = false;
        }
        // Formula columns should be hidden by default
        else if (col.id.startsWith("formula_")) {
          acc[col.id] = false;
        } else {
          // Always set required columns to true, otherwise default to visible
          acc[col.id] = true;
        }
        return acc;
      },
      {} as Record<string, boolean>,
    );
    setColumnVisibility(defaultVisibility);
  }, [availableCoreColumns, setColumnVisibility]);

  // Function to get visible column definitions
  const getVisibleColumnDefs = useCallback(
    <TData>(columnDefs: ColumnDef<TData>[]): ColumnDef<TData>[] => {
      const visibleColumnIds = new Set(
        Object.entries(columnVisibility)
          .filter(([, isVisible]) => isVisible)
          .map(([id]) => id),
      );

      return columnDefs.filter((col) => {
        const colId = col.id || "";
        const meta = col.meta as { required?: boolean } | undefined;

        // Always show required columns
        if (meta?.required) return true;

        return visibleColumnIds.has(colId);
      });
    },
    [columnVisibility],
  );

  return {
    availableColumns,
    selectedColumns,
    columnVisibility,
    isColumnSelected,
    toggleColumn,
    resetToDefault,
    getVisibleColumnDefs,
    setColumnVisibility,
    isLoading: false,
    error: null,
  };
}

/**
 * Hook specifically for managing channel column selections.
 * Note: Channels don't have custom fields, so we provide a simplified version.
 *
 * @param coreColumns - The core column definitions for channels
 * @param selectedMetrics - Optional array of selected metric keys
 * @returns Methods and state for managing selected channel columns
 */
export function useChannelColumnSelection<T = Record<string, unknown>>(
  coreColumns: UnifiedTableColumn<T>[],
  selectedMetrics?: string[],
) {
  // Keys for sessionStorage
  const columnsVisibilityKey = `channels::list::column-visibility`;

  const [columnVisibility, setColumnVisibility] = useTrackedSessionStorage<
    Record<string, boolean>
  >(
    columnsVisibilityKey,
    // By default, show all core columns
    coreColumns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  // Convert core columns to SelectableColumn format
  const availableCoreColumns = useMemo<SelectableColumn[]>(
    () =>
      coreColumns
        .filter((col) => !col.exportOnly)
        .map((col) => ({
          id: col.key.toString(),
          title: col.title,
          columnType: "core" as const,
          required: col.required || false,
          defaultHidden: col.defaultHidden,
        })),
    [coreColumns],
  );

  // Convert metric columns to SelectableColumn format
  const availableMetricColumns = useMemo<SelectableColumn[]>(() => {
    if (!selectedMetrics || selectedMetrics.length === 0) return [];

    return selectedMetrics.map((metricKey) => ({
      id: `metric_${metricKey}`,
      title: metricKey, // Title will be provided by the column definition
      columnType: "metric" as const,
      required: false,
    }));
  }, [selectedMetrics]);

  // All available columns (core + metrics, no custom fields for channels)
  const availableColumns = useMemo<SelectableColumn[]>(
    () => [...availableCoreColumns, ...availableMetricColumns],
    [availableCoreColumns, availableMetricColumns],
  );

  // Toggle core column visibility
  const toggleColumn = useCallback(
    (columnId: string) => {
      // Check if it's a required column
      const column = availableCoreColumns.find((col) => col.id === columnId);
      if (column?.required) return; // Can't toggle required columns

      setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !(prev?.[columnId] ?? false),
      }));
    },
    [availableCoreColumns, setColumnVisibility],
  );

  // Check if a column is selected/visible
  const isColumnSelected = useCallback(
    (columnId: string) => {
      // Metric columns are always visible when in selectedMetrics
      if (columnId.startsWith("metric_")) {
        return true;
      }
      return columnVisibility[columnId] === true;
    },
    [columnVisibility],
  );

  return {
    availableColumns,
    isColumnSelected,
    toggleColumn,
    isLoading: false, // No async loading needed for channels
    error: null, // No errors since we don't fetch field definitions
  };
}
